import { CartAddEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class HsBoughtTogether extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;
    this.addEventListener('change', this.#update, { signal });
    this.querySelector('[data-hs-bt-submit]')?.addEventListener('click', this.#addToCart, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#syncCurrentVariant, { signal });

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  async #load() {
    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const template = html.querySelector('template[data-hs-bt-items]');
      const items = template?.content.querySelectorAll('[data-hs-bt-item]');
      if (!items?.length) return;
      this.querySelector('[data-hs-bt-list]')?.append(...items);
      const inner = this.querySelector('[data-hs-bt-inner]');
      if (inner instanceof HTMLElement) inner.hidden = false;
      this.#update();
    } catch (error) {
      console.error(error);
    }
  }

  get #selectedItems() {
    return [...this.querySelectorAll('[data-hs-bt-item]')].filter((item) => {
      const check = item.querySelector('[data-hs-bt-check]');
      return !check || (check instanceof HTMLInputElement && check.checked);
    });
  }

  #format(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  #update = () => {
    let total = 0;

    for (const item of this.querySelectorAll('[data-hs-bt-item]')) {
      const select = item.querySelector('[data-hs-bt-select]');
      if (!(select instanceof HTMLSelectElement)) continue;
      const price = Number(select.selectedOptions[0]?.dataset.price || 0);
      const priceElement = item.querySelector('[data-hs-bt-price]');
      if (priceElement) priceElement.textContent = this.#format(price);
    }

    for (const item of this.#selectedItems) {
      const select = item.querySelector('[data-hs-bt-select]');
      if (select instanceof HTMLSelectElement) total += Number(select.selectedOptions[0]?.dataset.price || 0);
    }

    const totalElement = this.querySelector('[data-hs-bt-total]');
    if (totalElement) totalElement.textContent = this.#format(total);

    const submit = this.querySelector('[data-hs-bt-submit]');
    if (submit instanceof HTMLButtonElement) submit.disabled = this.#selectedItems.length === 0;
  };

  #syncCurrentVariant = (event) => {
    const variant = event.detail?.resource;
    if (!variant?.id || event.detail?.data?.productId !== this.dataset.productId) return;
    const select = this.querySelector('[data-current] [data-hs-bt-select]');
    if (!(select instanceof HTMLSelectElement)) return;
    const option = [...select.options].find((opt) => opt.value === String(variant.id));
    if (!option || option.disabled) return;
    select.value = option.value;
    this.#update();
  };

  #addToCart = async () => {
    const submit = this.querySelector('[data-hs-bt-submit]');
    const error = this.querySelector('[data-hs-bt-error]');
    const items = this.#selectedItems
      .map((item) => item.querySelector('[data-hs-bt-select]'))
      .filter((select) => select instanceof HTMLSelectElement)
      .map((select) => ({ id: Number(select.value), quantity: 1 }));

    if (!items.length || !(submit instanceof HTMLButtonElement)) return;

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        if (error) {
          error.textContent = result.description || result.message || '';
          error.hidden = false;
        }
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'hs-bought-together', {
          source: 'hs-bought-together',
          itemCount: cart.item_count,
          productId: this.dataset.productId,
        })
      );
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && !drawer.hasAttribute('auto-open')) drawer.open();
    } catch (err) {
      console.error(err);
    } finally {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('hs-bought-together')) {
  customElements.define('hs-bought-together', HsBoughtTogether);
}
